import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AssignCustomScheduleOptions,
  AssignCustomScheduleResponse,
  AssignWorkingHoursScheduleOptions,
  AssignWorkingHoursScheduleResponse,
  BulkUpdateStaffMemberTagsApplicationErrors,
  BulkUpdateStaffMemberTagsByFilterApplicationErrors,
  BulkUpdateStaffMemberTagsByFilterOptions,
  BulkUpdateStaffMemberTagsByFilterResponse,
  BulkUpdateStaffMemberTagsOptions,
  BulkUpdateStaffMemberTagsResponse,
  ConnectStaffMemberToUserOptions,
  ConnectStaffMemberToUserResponse,
  CountStaffMembersOptions,
  CountStaffMembersResponse,
  CreateStaffMemberOptions,
  DisconnectStaffMemberFromUserOptions,
  DisconnectStaffMemberFromUserResponse,
  GetDeletedStaffMemberOptions,
  GetDeletedStaffMemberResponse,
  GetStaffMemberOptions,
  ListDeletedStaffMembersOptions,
  ListDeletedStaffMembersResponse,
  QueryStaffMembersOptions,
  QueryStaffMembersResponse,
  SearchStaffMembersOptions,
  SearchStaffMembersResponse,
  StaffMember,
  StaffMemberConnectedToUserEnvelope,
  StaffMemberCreatedEnvelope,
  StaffMemberDeletedEnvelope,
  StaffMemberDisconnectedFromUserEnvelope,
  StaffMemberFullyCreatedEnvelope,
  StaffMemberQuery,
  StaffMemberSearch,
  StaffMemberTagsModifiedEnvelope,
  StaffMemberUpdatedEnvelope,
  StaffMembersQueryBuilder,
  UpdateStaffMember,
  UpdateStaffMemberOptions,
  assignCustomSchedule as universalAssignCustomSchedule,
  assignWorkingHoursSchedule as universalAssignWorkingHoursSchedule,
  bulkUpdateStaffMemberTags as universalBulkUpdateStaffMemberTags,
  bulkUpdateStaffMemberTagsByFilter as universalBulkUpdateStaffMemberTagsByFilter,
  connectStaffMemberToUser as universalConnectStaffMemberToUser,
  countStaffMembers as universalCountStaffMembers,
  createStaffMember as universalCreateStaffMember,
  deleteStaffMember as universalDeleteStaffMember,
  disconnectStaffMemberFromUser as universalDisconnectStaffMemberFromUser,
  getDeletedStaffMember as universalGetDeletedStaffMember,
  getStaffMember as universalGetStaffMember,
  listDeletedStaffMembers as universalListDeletedStaffMembers,
  queryStaffMembers as universalQueryStaffMembers,
  removeStaffMemberFromTrashBin as universalRemoveStaffMemberFromTrashBin,
  searchStaffMembers as universalSearchStaffMembers,
  typedQueryStaffMembers as universalTypedQueryStaffMembers,
  updateStaffMember as universalUpdateStaffMember,
} from './bookings-staff-v1-staff-member-staff-members.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/bookings' };

export function createStaffMember(
  httpClient: HttpClient
): CreateStaffMemberSignature {
  return (
    staffMember: NonNullablePaths<StaffMember, `name`, 2>,
    options?: CreateStaffMemberOptions
  ) =>
    universalCreateStaffMember(
      staffMember,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateStaffMemberSignature {
  /**
   * Creates a staff member.
   *
   *
   * By default, the staff member works during the business working hours.
   * You could follow this [sample flow](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/sample-flows#assign-a-staff-working-hour-schedule) to set custom working hours.
   * @param - Staff member to create.
   * @param - Options for creating the staff member.
   * @returns Created staff member.
   */
  (
    staffMember: NonNullablePaths<StaffMember, `name`, 2>,
    options?: CreateStaffMemberOptions
  ): Promise<
    NonNullablePaths<
      StaffMember,
      | `resource.workingHoursSchedules`
      | `resource.workingHoursSchedules.${number}.shared`
      | `resource.usesDefaultWorkingHours`
      | `associatedWixIdentity.identificationData.anonymousVisitorId`
      | `associatedWixIdentity.identificationData.memberId`
      | `associatedWixIdentity.identificationData.wixUserId`
      | `associatedWixIdentity.identificationData.appId`
      | `associatedWixIdentity.connectionStatus`
      | `associatedWixIdentity.connection.status`
      | `tags.privateTags.tagIds`,
      5
    >
  >;
}

export function getStaffMember(
  httpClient: HttpClient
): GetStaffMemberSignature {
  return (staffMemberId: string, options?: GetStaffMemberOptions) =>
    universalGetStaffMember(
      staffMemberId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetStaffMemberSignature {
  /**
   * Retrieves a staff member.
   * @param - ID of the staff member to retrieve.
   * @param - Option for retrieving the staff member.
   * @returns Retrieved staff member.
   */
  (staffMemberId: string, options?: GetStaffMemberOptions): Promise<
    NonNullablePaths<
      StaffMember,
      | `resource.workingHoursSchedules`
      | `resource.workingHoursSchedules.${number}.shared`
      | `resource.usesDefaultWorkingHours`
      | `associatedWixIdentity.identificationData.anonymousVisitorId`
      | `associatedWixIdentity.identificationData.memberId`
      | `associatedWixIdentity.identificationData.wixUserId`
      | `associatedWixIdentity.identificationData.appId`
      | `associatedWixIdentity.connectionStatus`
      | `associatedWixIdentity.connection.status`
      | `tags.privateTags.tagIds`,
      5
    >
  >;
}

export function getDeletedStaffMember(
  httpClient: HttpClient
): GetDeletedStaffMemberSignature {
  return (staffMemberId: string, options?: GetDeletedStaffMemberOptions) =>
    universalGetDeletedStaffMember(
      staffMemberId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetDeletedStaffMemberSignature {
  /**
   * Retrieves a deleted staff member from the trash bin.
   * @param - ID of the deleted staff member to retrieve.
   * @param - Options for getting the deleted staff member.
   */
  (staffMemberId: string, options?: GetDeletedStaffMemberOptions): Promise<
    NonNullablePaths<
      GetDeletedStaffMemberResponse,
      | `staffMember.resource.workingHoursSchedules`
      | `staffMember.resource.workingHoursSchedules.${number}.shared`
      | `staffMember.resource.usesDefaultWorkingHours`
      | `staffMember.associatedWixIdentity.identificationData.anonymousVisitorId`
      | `staffMember.associatedWixIdentity.identificationData.memberId`
      | `staffMember.associatedWixIdentity.identificationData.wixUserId`
      | `staffMember.associatedWixIdentity.identificationData.appId`
      | `staffMember.associatedWixIdentity.connectionStatus`
      | `staffMember.associatedWixIdentity.connection.status`
      | `staffMember.tags.privateTags.tagIds`,
      6
    >
  >;
}

export function listDeletedStaffMembers(
  httpClient: HttpClient
): ListDeletedStaffMembersSignature {
  return (options?: ListDeletedStaffMembersOptions) =>
    universalListDeletedStaffMembers(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListDeletedStaffMembersSignature {
  /**
   * Retrieves a list of deleted staff members from the trash bin.
   *
   *
   * List Deleted Staff Members uses these defaults, which you can override:
   * - `paging.limit` set to `100`.
   * - Only staff member fields are returned. Specify the `fields` parameter to include additional data about the staff member's associated resource.
   * @param - Options for listing deleted staff members.
   */
  (options?: ListDeletedStaffMembersOptions): Promise<
    NonNullablePaths<
      ListDeletedStaffMembersResponse,
      | `staffMembers`
      | `staffMembers.${number}.resource.usesDefaultWorkingHours`
      | `staffMembers.${number}.associatedWixIdentity.identificationData.anonymousVisitorId`
      | `staffMembers.${number}.associatedWixIdentity.identificationData.memberId`
      | `staffMembers.${number}.associatedWixIdentity.identificationData.wixUserId`
      | `staffMembers.${number}.associatedWixIdentity.identificationData.appId`
      | `staffMembers.${number}.associatedWixIdentity.connectionStatus`
      | `staffMembers.${number}.associatedWixIdentity.connection.status`,
      6
    >
  >;
}

export function removeStaffMemberFromTrashBin(
  httpClient: HttpClient
): RemoveStaffMemberFromTrashBinSignature {
  return (staffMemberId: string) =>
    universalRemoveStaffMemberFromTrashBin(
      staffMemberId,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveStaffMemberFromTrashBinSignature {
  /**
   * Removes a staff member from the trash bin, permanently deleting it.
   * @param - ID of the staff member to delete permanently.
   */
  (staffMemberId: string): Promise<void>;
}

export function updateStaffMember(
  httpClient: HttpClient
): UpdateStaffMemberSignature {
  return (
    _id: string,
    staffMember: NonNullablePaths<UpdateStaffMember, `revision`, 2>,
    options?: UpdateStaffMemberOptions
  ) =>
    universalUpdateStaffMember(
      _id,
      staffMember,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateStaffMemberSignature {
  /**
   * Updates a staff member.
   *
   *
   * Each time the staff member is updated, `revision` increments by 1.
   * You must include current revision of the staff member when updating it.
   * This ensures you're working with the latest service information and prevents unintended overwrites.
   * @param - Staff member ID.
   * @param - The staff member to update
   * @param - Options for updating the staff member.
   * @returns Updated staff member.
   */
  (
    _id: string,
    staffMember: NonNullablePaths<UpdateStaffMember, `revision`, 2>,
    options?: UpdateStaffMemberOptions
  ): Promise<
    NonNullablePaths<
      StaffMember,
      | `resource.workingHoursSchedules`
      | `resource.workingHoursSchedules.${number}.shared`
      | `resource.usesDefaultWorkingHours`
      | `associatedWixIdentity.identificationData.anonymousVisitorId`
      | `associatedWixIdentity.identificationData.memberId`
      | `associatedWixIdentity.identificationData.wixUserId`
      | `associatedWixIdentity.identificationData.appId`
      | `associatedWixIdentity.connectionStatus`
      | `associatedWixIdentity.connection.status`
      | `tags.privateTags.tagIds`,
      5
    >
  >;
}

export function deleteStaffMember(
  httpClient: HttpClient
): DeleteStaffMemberSignature {
  return (staffMemberId: string) =>
    universalDeleteStaffMember(
      staffMemberId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteStaffMemberSignature {
  /**
   * Deletes a staff member.
   *
   *
   * Also deletes the [resource](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction) associated with the staff member.
   * @param - ID of the staff member to delete.
   */
  (staffMemberId: string): Promise<void>;
}

export function queryStaffMembers(
  httpClient: HttpClient
): QueryStaffMembersSignature {
  return (options?: QueryStaffMembersOptions) =>
    universalQueryStaffMembers(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryStaffMembersSignature {
  /**
   * Creates a query to retrieve a list of staff members.
   *
   * The `queryStaffMembers()` function builds a query to retrieve a list of staff members and returns a `StaffMembersQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the [find()](https://dev.wix.com/docs/sdk/backend-modules/bookings/staff-members/staff-members-query-builder/find) function.
   *
   * You can refine the query by chaining `StaffMembersQueryBuilder` functions onto the query. `StaffMembersQueryBuilder` functions enable you to sort, filter, and control the results that `queryStaffMembers()` returns.
   *
   * `queryStaffMembers()` runs with the following `StaffMembersQueryBuilder` default that you can override:
   *
   * + `limit` is `50`.
   * + Sorted by `id` in ascending order.
   *
   * The functions that are chained to `staffMembersTypes()` are applied in the order they are called.
   *
   * The following `StaffMembersQueryBuilder` functions are supported for the `queryStaffMembers()` function. For a full description of the resource object, see the object returned for the [items](https://dev.wix.com/docs/sdk/backend-modules/bookings/staff-members/staff-members-query-result/items) property in `StaffMembersQueryResult`.
   */
  (options?: QueryStaffMembersOptions): StaffMembersQueryBuilder;
}

export function typedQueryStaffMembers(
  httpClient: HttpClient
): TypedQueryStaffMembersSignature {
  return (query: StaffMemberQuery, options?: QueryStaffMembersOptions) =>
    universalTypedQueryStaffMembers(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryStaffMembersSignature {
  /** */
  (query: StaffMemberQuery, options?: QueryStaffMembersOptions): Promise<
    NonNullablePaths<
      QueryStaffMembersResponse,
      | `staffMembers`
      | `staffMembers.${number}.resource.usesDefaultWorkingHours`
      | `staffMembers.${number}.associatedWixIdentity.identificationData.anonymousVisitorId`
      | `staffMembers.${number}.associatedWixIdentity.identificationData.memberId`
      | `staffMembers.${number}.associatedWixIdentity.identificationData.wixUserId`
      | `staffMembers.${number}.associatedWixIdentity.identificationData.appId`
      | `staffMembers.${number}.associatedWixIdentity.connectionStatus`
      | `staffMembers.${number}.associatedWixIdentity.connection.status`,
      6
    >
  >;
}

export function countStaffMembers(
  httpClient: HttpClient
): CountStaffMembersSignature {
  return (options?: CountStaffMembersOptions) =>
    universalCountStaffMembers(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CountStaffMembersSignature {
  /**
   * Counts how many staff members match the given filter.
   *
   *
   * See [Query Staff Members](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/query-staff-members) for a list of supported filters.
   *
   * ### See also
   *
   * To learn about working with filters in general, see [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  (options?: CountStaffMembersOptions): Promise<
    NonNullablePaths<CountStaffMembersResponse, `count`, 2>
  >;
}

export function connectStaffMemberToUser(
  httpClient: HttpClient
): ConnectStaffMemberToUserSignature {
  return (staffMemberId: string, options?: ConnectStaffMemberToUserOptions) =>
    universalConnectStaffMemberToUser(
      staffMemberId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ConnectStaffMemberToUserSignature {
  /**
   * Connects a Wix user to a staff member, enabling them to manage their own working hour schedule in the dashboard.
   *
   *
   * By default, Wix Bookings uses the staff member's `email`.
   * However, you can specify an alternative email address. If no existing Wix user is associated with that email, Wix sends them an invitation to become a Wix user.
   * If an existing user is found but not linked to the site, Wix Bookings sends an invitation to join the site.
   *
   * To check the connection status, call [Get Staff Member](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/get-staff-member), and specify `ASSOCIATED_IDENTITY_STATUS` in the `fields` parameter.
   *
   * You must call [Disconnect Staff Member From User](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/disconnect-staff-member-from-user) before connecting a different Wix user to the staff member.
   * @param - ID of the staff member to connect to the Wix user.
   * @param - Options for connecting the staff member to a Wix user.
   */
  (staffMemberId: string, options?: ConnectStaffMemberToUserOptions): Promise<
    NonNullablePaths<
      ConnectStaffMemberToUserResponse,
      | `staffMember.resource.workingHoursSchedules`
      | `staffMember.resource.workingHoursSchedules.${number}.shared`
      | `staffMember.resource.usesDefaultWorkingHours`
      | `staffMember.associatedWixIdentity.identificationData.anonymousVisitorId`
      | `staffMember.associatedWixIdentity.identificationData.memberId`
      | `staffMember.associatedWixIdentity.identificationData.wixUserId`
      | `staffMember.associatedWixIdentity.identificationData.appId`
      | `staffMember.associatedWixIdentity.connectionStatus`
      | `staffMember.associatedWixIdentity.connection.status`
      | `staffMember.tags.privateTags.tagIds`,
      6
    >
  >;
}

export function searchStaffMembers(
  httpClient: HttpClient
): SearchStaffMembersSignature {
  return (search: StaffMemberSearch, options?: SearchStaffMembersOptions) =>
    universalSearchStaffMembers(
      search,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchStaffMembersSignature {
  /**
   * Retrieves a list of up to 100 staff members, given the provided filtering, paging, and sorting.
   *
   *
   * ### Defaults
   *
   * Search Staff Members has the following default settings, which you can override:
   * + Sorted by `createdDate` in ascending order.
   * + `cursorPaging.limit` set to `100`.
   *
   * ### See also
   *
   * To learn about working with Search methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language) and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
   * @param - Search criteria including filter, sort, and paging options.
   */
  (search: StaffMemberSearch, options?: SearchStaffMembersOptions): Promise<
    NonNullablePaths<
      SearchStaffMembersResponse,
      | `staffMembers`
      | `staffMembers.${number}.resource.usesDefaultWorkingHours`
      | `staffMembers.${number}.associatedWixIdentity.identificationData.anonymousVisitorId`
      | `staffMembers.${number}.associatedWixIdentity.identificationData.memberId`
      | `staffMembers.${number}.associatedWixIdentity.identificationData.wixUserId`
      | `staffMembers.${number}.associatedWixIdentity.identificationData.appId`
      | `staffMembers.${number}.associatedWixIdentity.connectionStatus`
      | `staffMembers.${number}.associatedWixIdentity.connection.status`
      | `aggregationData.results`
      | `aggregationData.results.${number}.scalar.type`
      | `aggregationData.results.${number}.scalar.value`
      | `aggregationData.results.${number}.name`
      | `aggregationData.results.${number}.type`
      | `aggregationData.results.${number}.fieldPath`,
      6
    >
  >;
}

export function disconnectStaffMemberFromUser(
  httpClient: HttpClient
): DisconnectStaffMemberFromUserSignature {
  return (
    staffMemberId: string,
    options?: DisconnectStaffMemberFromUserOptions
  ) =>
    universalDisconnectStaffMemberFromUser(
      staffMemberId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DisconnectStaffMemberFromUserSignature {
  /**
   * Disconnects a staff member from a Wix user, clearing the `associatedWixIdentity` field.
   *
   *
   * Consequently, the user can no longer manage the staff member's working hour schedule in the dashboard.
   *
   * You must call Disconnect Staff Member From User before connecting a different Wix user to the staff member.
   * @param - ID of the staff member to disconnect from its Wix user.
   * @param - Options to disconnect the staff member from a Wix user.
   */
  (
    staffMemberId: string,
    options?: DisconnectStaffMemberFromUserOptions
  ): Promise<
    NonNullablePaths<
      DisconnectStaffMemberFromUserResponse,
      | `staffMember.resource.workingHoursSchedules`
      | `staffMember.resource.workingHoursSchedules.${number}.shared`
      | `staffMember.resource.usesDefaultWorkingHours`
      | `staffMember.associatedWixIdentity.identificationData.anonymousVisitorId`
      | `staffMember.associatedWixIdentity.identificationData.memberId`
      | `staffMember.associatedWixIdentity.identificationData.wixUserId`
      | `staffMember.associatedWixIdentity.identificationData.appId`
      | `staffMember.associatedWixIdentity.connectionStatus`
      | `staffMember.associatedWixIdentity.connection.status`
      | `staffMember.tags.privateTags.tagIds`,
      6
    >
  >;
}

export function assignWorkingHoursSchedule(
  httpClient: HttpClient
): AssignWorkingHoursScheduleSignature {
  return (
    staffMemberId: string,
    scheduleId: string,
    options?: AssignWorkingHoursScheduleOptions
  ) =>
    universalAssignWorkingHoursSchedule(
      staffMemberId,
      scheduleId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AssignWorkingHoursScheduleSignature {
  /**
   * Updates a staff member's working hours based on the specified [schedule ID](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction).
   *
   *
   * ### Default working hours
   *
   * By default, staff members work during the opening hours of the business's default [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).
   *
   * ### Schedule ID
   *
   * You can specify either the ID of the business's working hour schedule or the  staff member's event schedule.
   * The call fails if you specify a different schedule ID.
   *
   * #### Staff event schedule
   *
   * To customize a staff member's working hours, specify their event schedule ID as `scheduleId`.
   * Refer to this [sample flow](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/sample-flows#assign-a-staff-working-hour-schedule) for more details.
   *
   * #### Business's working hour schedule
   *
   * To reset a staff member's working hours to the default business hours, specify the ID of the business's working hour schedule as `scheduleId`.
   * @param - ID of the staff member to assign the schedule to.
   * @param - ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)
   * to assign to the staff member.
   *
   * Must be either the staff member's event schedule ID or the working hour
   * schedule ID for a business location.
   * @param - Options for setting the staff member's working hours schedule.
   */
  (
    staffMemberId: string,
    scheduleId: string,
    options?: AssignWorkingHoursScheduleOptions
  ): Promise<
    NonNullablePaths<
      AssignWorkingHoursScheduleResponse,
      | `staffMember.resource.workingHoursSchedules`
      | `staffMember.resource.workingHoursSchedules.${number}.shared`
      | `staffMember.resource.usesDefaultWorkingHours`
      | `staffMember.associatedWixIdentity.identificationData.anonymousVisitorId`
      | `staffMember.associatedWixIdentity.identificationData.memberId`
      | `staffMember.associatedWixIdentity.identificationData.wixUserId`
      | `staffMember.associatedWixIdentity.identificationData.appId`
      | `staffMember.associatedWixIdentity.connectionStatus`
      | `staffMember.associatedWixIdentity.connection.status`
      | `staffMember.tags.privateTags.tagIds`,
      6
    >
  >;
}

export function assignCustomSchedule(
  httpClient: HttpClient
): AssignCustomScheduleSignature {
  return (
    staffMemberId: string,
    scheduleId: string,
    options?: AssignCustomScheduleOptions
  ) =>
    universalAssignCustomSchedule(
      staffMemberId,
      scheduleId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AssignCustomScheduleSignature {
  /**
   * Assigns a custom working hours schedule to the staff member
   *
   *
   * The working hours schedule is a schedule that defines the working hours of a staff member, and dictate when the staff member is available for bookings.
   *
   * By default staff members use the shared business working hours schedule.
   * By assigning a custom working hours schedule to a staff member, you can define specific working hours for that staff member.
   *
   * To create and manage schedules and working hours sessions, use the [Events API](https://dev.wix.com/docs/api-reference/business-management/calendar/events-v3/introduction).
   * @param - ID of the staff member for which to assign a working hour schedule.
   * @param - ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)
   * to assign to the staff member.
   * @deprecated
   */
  (
    staffMemberId: string,
    scheduleId: string,
    options?: AssignCustomScheduleOptions
  ): Promise<
    NonNullablePaths<
      AssignCustomScheduleResponse,
      | `staffMember.resource.workingHoursSchedules`
      | `staffMember.resource.workingHoursSchedules.${number}.shared`
      | `staffMember.resource.usesDefaultWorkingHours`
      | `staffMember.associatedWixIdentity.identificationData.anonymousVisitorId`
      | `staffMember.associatedWixIdentity.identificationData.memberId`
      | `staffMember.associatedWixIdentity.identificationData.wixUserId`
      | `staffMember.associatedWixIdentity.identificationData.appId`
      | `staffMember.associatedWixIdentity.connectionStatus`
      | `staffMember.associatedWixIdentity.connection.status`
      | `staffMember.tags.privateTags.tagIds`,
      6
    >
  >;
}

export function bulkUpdateStaffMemberTags(
  httpClient: HttpClient
): BulkUpdateStaffMemberTagsSignature {
  return (ids: string[], options?: BulkUpdateStaffMemberTagsOptions) =>
    universalBulkUpdateStaffMemberTags(
      ids,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateStaffMemberTagsSignature {
  /**
   * Synchronously updates [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction) for up to 100 staff members.
   *
   *
   * If you specify a tag both in `assignTags` and `unassignTags`, the call succeeds and the tag is assigned.
   * @param - IDs of staff members to update tags for.
   */
  (ids: string[], options?: BulkUpdateStaffMemberTagsOptions): Promise<
    NonNullablePaths<
      BulkUpdateStaffMemberTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateStaffMemberTagsApplicationErrors;
    }
  >;
}

export function bulkUpdateStaffMemberTagsByFilter(
  httpClient: HttpClient
): BulkUpdateStaffMemberTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateStaffMemberTagsByFilterOptions
  ) =>
    universalBulkUpdateStaffMemberTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateStaffMemberTagsByFilterSignature {
  /**
   * Asynchronously updates [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction) for staff members, given the provided filtering.
   *
   *
   * If you specify a tag both in `assignTags` and `unassignTags`, the call succeeds and the tag is assigned.
   *
   * See [Query Staff Members](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/query-staff-members) for a list of supported filters.
   * @param - Filter to base the update upon.
   * See [Query Staff Members](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/query-staff-members)
   * for a complete list of supported filters.
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateStaffMemberTagsByFilterOptions
  ): Promise<
    NonNullablePaths<BulkUpdateStaffMemberTagsByFilterResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkUpdateStaffMemberTagsByFilterApplicationErrors;
    }
  >;
}

export const onStaffMemberConnectedToUser = EventDefinition(
  'wix.bookings.staff.v1.staff_member_connected_to_user',
  true,
  (event: StaffMemberConnectedToUserEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.staffMember.createdDate' },
            { path: 'data.staffMember.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'data.staffMember.mainMedia.image' }],
        },
      ])
    )
)<StaffMemberConnectedToUserEnvelope>();
export const onStaffMemberCreated = EventDefinition(
  'wix.bookings.staff.v1.staff_member_created',
  true,
  (event: StaffMemberCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.mainMedia.image' }],
        },
      ])
    )
)<StaffMemberCreatedEnvelope>();
export const onStaffMemberDeleted = EventDefinition(
  'wix.bookings.staff.v1.staff_member_deleted',
  true,
  (event: StaffMemberDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.mainMedia.image' }],
        },
      ])
    )
)<StaffMemberDeletedEnvelope>();
export const onStaffMemberDisconnectedFromUser = EventDefinition(
  'wix.bookings.staff.v1.staff_member_disconnected_from_user',
  true,
  (event: StaffMemberDisconnectedFromUserEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.staffMember.createdDate' },
            { path: 'data.staffMember.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'data.staffMember.mainMedia.image' }],
        },
      ])
    )
)<StaffMemberDisconnectedFromUserEnvelope>();
export const onStaffMemberFullyCreated = EventDefinition(
  'wix.bookings.staff.v1.staff_member_fully_created',
  true,
  (event: StaffMemberFullyCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.staffMember.createdDate' },
            { path: 'data.staffMember.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'data.staffMember.mainMedia.image' }],
        },
      ])
    )
)<StaffMemberFullyCreatedEnvelope>();
export const onStaffMemberTagsModified = EventDefinition(
  'wix.bookings.staff.v1.staff_member_tags_modified',
  true,
  (event: StaffMemberTagsModifiedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.staffMember.createdDate' },
            { path: 'data.staffMember.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'data.staffMember.mainMedia.image' }],
        },
      ])
    )
)<StaffMemberTagsModifiedEnvelope>();
export const onStaffMemberUpdated = EventDefinition(
  'wix.bookings.staff.v1.staff_member_updated',
  true,
  (event: StaffMemberUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.mainMedia.image' },
            { path: 'modifiedFields.mainMedia.image' },
          ],
        },
      ])
    )
)<StaffMemberUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Address,
  AddressLocation,
  AddressStreetOneOf,
  AggregationData,
  AggregationResults,
  AggregationResultsResultOneOf,
  AggregationResultsScalarResult,
  AggregationType,
  ApplicationError,
  ApprovalStatus,
  AssignCustomScheduleOptions,
  AssignCustomScheduleRequest,
  AssignCustomScheduleResponse,
  AssignWorkingHoursScheduleOptions,
  AssignWorkingHoursScheduleRequest,
  AssignWorkingHoursScheduleResponse,
  AssociatedConferencingAccount,
  AssociatedConferencingAccountAccountOneOf,
  AssociatedConferencingAccounts,
  AssociatedConferencingProvider,
  AssociatedConferencingProviders,
  AssociatedWixIdentity,
  AssociatedWixIdentityConnectionStatusEnumConnectionStatus,
  Availability,
  AvailabilityConstraints,
  AvailabilityPolicy,
  AvailabilityPolicyUpdated,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkUpdateStaffMemberTagsByFilterOptions,
  BulkUpdateStaffMemberTagsByFilterRequest,
  BulkUpdateStaffMemberTagsByFilterResponse,
  BulkUpdateStaffMemberTagsOptions,
  BulkUpdateStaffMemberTagsRequest,
  BulkUpdateStaffMemberTagsResponse,
  BulkUpdateStaffMemberTagsResult,
  BusinessLocation,
  BusinessSchedule,
  CalendarConference,
  CalendarDateTime,
  CalendarType,
  CommonIdentificationData,
  CommonIdentificationDataIdOneOf,
  ConferenceProvider,
  ConferenceType,
  ConnectStaffMemberToUserOptions,
  ConnectStaffMemberToUserRequest,
  ConnectStaffMemberToUserResponse,
  Connection,
  ConnectionStatus,
  CountStaffMembersOptions,
  CountStaffMembersRequest,
  CountStaffMembersResponse,
  CreateStaffMemberOptions,
  CreateStaffMemberRequest,
  CreateStaffMemberResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  CursorSearch,
  CursorSearchPagingMethodOneOf,
  Cursors,
  CustomConferenceAccount,
  DateHistogramResult,
  DateHistogramResults,
  Day,
  DayOfWeek,
  DeleteStaffMemberRequest,
  DeleteStaffMemberResponse,
  DisconnectStaffMemberFromUserOptions,
  DisconnectStaffMemberFromUserRequest,
  DisconnectStaffMemberFromUserResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  EventSchedule,
  ExtendedFields,
  ExternalCalendarInfo,
  ExternalCalendarOverrides,
  Frequency,
  Gender,
  GetDeletedStaffMemberOptions,
  GetDeletedStaffMemberRequest,
  GetDeletedStaffMemberResponse,
  GetStaffMemberOptions,
  GetStaffMemberRequest,
  GetStaffMemberResponse,
  GroupByValueResults,
  IdentificationData,
  IdentificationDataIdOneOf,
  IdentityType,
  Interval,
  IntervalSplit,
  ItemMetadata,
  LinkedSchedule,
  ListDeletedStaffMembersOptions,
  ListDeletedStaffMembersRequest,
  ListDeletedStaffMembersResponse,
  LocalDateTime,
  Location,
  LocationOptions,
  LocationStatus,
  LocationType,
  LocationsAddress,
  LocationsAddressLocation,
  LocationsLocation,
  LocationsLocationType,
  LocationsStreetAddress,
  MediaItem,
  MediaItemMediaOneOf,
  MessageEnvelope,
  MigrationData,
  MigrationEvent,
  Mode,
  MultipleSessionsCreated,
  NestedAggregationResults,
  NestedAggregationResultsResultOneOf,
  NestedResultValue,
  NestedResultValueResultOneOf,
  NestedResults,
  NestedValueAggregationResult,
  Participant,
  ParticipantNotification,
  PolicyRemovedFromContributor,
  PolicyUpdatedForContributor,
  Price,
  QueryStaffMembersMultiLanguageRequest,
  QueryStaffMembersMultiLanguageResponse,
  QueryStaffMembersOptions,
  QueryStaffMembersRequest,
  QueryStaffMembersResponse,
  RangeAggregationResult,
  RangeResult,
  RangeResults,
  Rate,
  RecurringInterval,
  RecurringIntervalType,
  RecurringSessionSplit,
  RecurringSessionsUpdated,
  RemoveStaffMemberFromTrashBinRequest,
  RemoveStaffMemberFromTrashBinResponse,
  RequestedFields,
  Resource,
  RestoreInfo,
  RestoreStaffMemberFromTrashBinRequest,
  RestoreStaffMemberFromTrashBinResponse,
  RestoreStaffRequest,
  RestoreStaffResponse,
  Results,
  ScalarResult,
  ScalarType,
  Schedule,
  ScheduleCancelled,
  ScheduleCreated,
  ScheduleNotification,
  ScheduleNotificationEventOneOf,
  ScheduleStatus,
  ScheduleUnassignedFromUser,
  ScheduleUpdated,
  ScheduleWithSessions,
  SearchDetails,
  SearchStaffMembersOptions,
  SearchStaffMembersRequest,
  SearchStaffMembersResponse,
  Session,
  SessionCancelled,
  SessionCreated,
  SessionType,
  SessionUpdated,
  SessionVersion,
  SitePropertiesOnScheduleCreation,
  SortOrder,
  Sorting,
  SpecialHourPeriod,
  SpecificLocation,
  SplitInterval,
  StaffData,
  StaffMember,
  StaffMemberConnectedToUser,
  StaffMemberConnectedToUserEnvelope,
  StaffMemberCreatedEnvelope,
  StaffMemberDeletedEnvelope,
  StaffMemberDisconnectedFromUser,
  StaffMemberDisconnectedFromUserEnvelope,
  StaffMemberFullyCreated,
  StaffMemberFullyCreatedEnvelope,
  StaffMemberQuerySpec,
  StaffMemberSearchSpec,
  StaffMemberTagsModifiedEnvelope,
  StaffMemberUpdatedEnvelope,
  StaffMembersQueryBuilder,
  StaffMembersQueryResult,
  Status,
  StreetAddress,
  Subdivision,
  TagList,
  Tags,
  TagsModified,
  TimePeriod,
  Transparency,
  UpdateStaffMember,
  UpdateStaffMemberOptions,
  UpdateStaffMemberRequest,
  UpdateStaffMemberResponse,
  ValueAggregationResult,
  ValueResult,
  ValueResults,
  Version,
  WebhookIdentityType,
  WorkingHoursSchedule,
  utils,
} from './bookings-staff-v1-staff-member-staff-members.universal.js';
